/**
 * PayPal return/cancel handler (Monetization v2, sc-6943). Called from
 * main.ts BEFORE the cabinet boots, mirroring the auth callback:
 *
 *   /billing/return  — PayPal sent the buyer back after they APPROVED.
 *       Day pass  (?token=<orderId>):  we CAPTURE the order (collects the
 *           money); the PAYMENT.CAPTURE.COMPLETED webhook grants the pass.
 *       Sub       (?subscription_id):  approval already fired the
 *           BILLING.SUBSCRIPTION.ACTIVATED webhook; nothing to capture.
 *       Either way we then POLL /v1/wallet briefly until the entitlement
 *           lands (the webhook is async), so the relaunch plays unlimited.
 *   /billing/cancel  — buyer bailed at PayPal. No charge, no grant; drop
 *       them back on the cabinet.
 *
 * The webhook is the source of truth for the grant — this page just
 * finalizes the money (day pass) and waits for the wallet to reflect it.
 * On completion the URL is rewritten back to `/` so a refresh doesn't
 * re-capture a consumed order, and the pending cartridge relaunches.
 */

import type { Wallet } from '@arcraz/arcadia-types';
import { captureOrder, getWallet } from '../api/client.js';

const RETURN_PATH = '/billing/return';
const CANCEL_PATH = '/billing/cancel';

/** How long to wait for the async webhook grant before giving up. */
const WALLET_POLL_ATTEMPTS = 8;
const WALLET_POLL_DELAY_MS = 750;

export interface BillingReturnOutcome {
  /** True if the URL was a billing return/cancel URL (whether or not the
   *  purchase completed). The caller uses this to decide boot flow. */
  handled: boolean;
  /** True if an active pass was confirmed — relaunch the pending cartridge. */
  entitled: boolean;
}

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Poll the wallet until an entitlement appears (webhook is async), or bail. */
async function waitForEntitlement(): Promise<boolean> {
  for (let i = 0; i < WALLET_POLL_ATTEMPTS; i += 1) {
    let wallet: Wallet | null = null;
    try {
      wallet = await getWallet();
    } catch {
      /* transient — try again */
    }
    if (wallet?.entitlement) return true;
    if (i < WALLET_POLL_ATTEMPTS - 1) await sleep(WALLET_POLL_DELAY_MS);
  }
  return false;
}

/**
 * Returns immediately if the URL isn't a billing return/cancel. Otherwise
 * finalizes the purchase and reports whether a pass is now active. Always
 * rewrites the URL back to `/` on completion so a reload boots cleanly.
 */
export async function runBillingReturnIfNeeded(): Promise<BillingReturnOutcome> {
  const path = window.location.pathname;

  if (path === CANCEL_PATH) {
    // Buyer bailed — nothing charged. Back to the cabinet, no relaunch.
    window.history.replaceState(null, '', '/');
    return { handled: true, entitled: false };
  }

  if (path !== RETURN_PATH) {
    return { handled: false, entitled: false };
  }

  const params = new URLSearchParams(window.location.search);
  // Day-pass Order id arrives as ?token=; subscriptions as ?subscription_id=.
  const orderId = params.get('token');
  const subscriptionId = params.get('subscription_id');

  try {
    if (orderId) {
      // Day pass: capture to collect the money. Tolerate a capture error
      // (e.g. already captured on a refresh) — the webhook is the grant
      // source of truth, so we still poll the wallet below.
      try {
        await captureOrder(orderId);
      } catch (err) {
        console.warn('[arcadia84] day-pass capture reported an error', { err });
      }
    } else if (!subscriptionId) {
      // Neither param — malformed return. Fall through to the wallet poll
      // anyway (harmless), then home.
      console.warn('[arcadia84] billing return missing token/subscription_id');
    }

    const entitled = await waitForEntitlement();
    window.history.replaceState(null, '', '/');
    return { handled: true, entitled };
  } catch (err) {
    console.warn('[arcadia84] billing return failed', { err });
    window.history.replaceState(null, '', '/');
    return { handled: true, entitled: false };
  }
}
